import { fetchAuthSession } from 'aws-amplify/auth'
import { ChatMessage } from '../components/chatbot/Chatbot'

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || 'https://6y3sgguhkh.execute-api.us-east-1.amazonaws.com/prod'

interface ApiResponse<T> {
  data?: T
  error?: string
  statusCode: number
}

class ApiService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    try {
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()
      
      return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    } catch (error) {
      return {
        'Content-Type': 'application/json'
      }
    }
  }

  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const headers = await this.getAuthHeaders()
      
      const url = `${API_BASE_URL}${endpoint}`
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      })

      let data = null
      let errorMessage: string | undefined
      
      try {
        data = await response.json()
      } catch {
        // Response body is not JSON
      }
      
      if (!response.ok) {
        errorMessage = (data as any)?.error || `HTTP ${response.status}: ${response.statusText}`
      }
      
      return {
        data,
        statusCode: response.status,
        error: errorMessage
      }
    } catch (error) {
      return {
        statusCode: 500,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  }

  // User Management
  async getUsers(): Promise<ApiResponse<any[]>> {
    return this.makeRequest('/admin/users')
  }

  async updateUserDataProducts(userId: string, dataProducts: string[]): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/users/${userId}/data-products`, {
      method: 'POST',
      body: JSON.stringify({ data_products: dataProducts })
    })
  }

  async getUserDevices(userId: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest(`/admin/users/${userId}/devices`)
  }

  // Device Management
  async registerDevice(deviceData: any): Promise<ApiResponse<any>> {
    return this.makeRequest('/admin/devices', {
      method: 'POST',
      body: JSON.stringify(deviceData)
    })
  }

  async getDevices(): Promise<ApiResponse<any[]>> {
    return this.makeRequest('/admin/devices')
  }

  async updateDeviceAuthorization(deviceId: string, dataProducts: string[]): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/devices/${deviceId}/authorization`, {
      method: 'PUT',
      body: JSON.stringify({ authorized_data_products: dataProducts })
    })
  }

  async revokeDevice(deviceId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/devices/${deviceId}/revoke`, {
      method: 'POST'
    })
  }

  async deleteDevice(deviceId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/devices/${deviceId}`, {
      method: 'DELETE'
    })
  }

  // Location Management
  async getDefaultHfLocations(): Promise<ApiResponse<any>> {
    return this.makeRequest('/admin/default-locations/hf_propagation')
  }

  async getLocations(dataProduct: string, search?: string): Promise<ApiResponse<any[]>> {
    let url = `/admin/locations/${dataProduct}?available=true`
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`
    }
    return this.makeRequest(url)
  }

  async createLocationList(userId: string, locationListData: any): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/users/${userId}/location-lists`, {
      method: 'POST',
      body: JSON.stringify(locationListData)
    })
  }

  async updateLocationList(userId: string, dataProduct: string, locationListData: any): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/users/${userId}/location-lists/${dataProduct}`, {
      method: 'PUT',
      body: JSON.stringify(locationListData)
    })
  }

  async getUserLocationList(userId: string, dataProduct: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/users/${userId}/location-lists/${dataProduct}`)
  }

  async getUserLocationLists(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/users/${userId}/location-lists`)
  }

  async deleteLocationList(userId: string, dataProduct: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/users/${userId}/location-lists/${dataProduct}`, {
      method: 'DELETE'
    })
  }

  async assignLocationListToDevice(deviceId: string, userId: string, dataProduct: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/devices/${deviceId}/location-list`, {
      method: 'PUT',
      body: JSON.stringify({ 
        user_id: userId,
        data_product: dataProduct 
      })
    })
  }

  async unassignLocationListFromDevice(deviceId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/devices/${deviceId}/location-list`, {
      method: 'PUT',
      body: JSON.stringify({ action: 'unassign' })
    })
  }

  async updateDeviceConfiguration(deviceId: string, configuration: any): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/devices/${deviceId}/configuration`, {
      method: 'PUT',
      body: JSON.stringify(configuration)
    })
  }

  // Diagnostics
  async getDiagnostics(dataProduct: string, summaryOnly: boolean = false): Promise<ApiResponse<any>> {
    const params = summaryOnly ? '?summary_only=true' : ''
    return this.makeRequest(`/admin/diagnostics/${dataProduct}${params}`)
  }

  async getAdminCounts(): Promise<ApiResponse<any>> {
    return this.makeRequest('/admin/diagnostics/admin_counts')
  }

  async getDiagnosticsHistory(dataProduct: string, pageSize: number = 50, nextToken?: string, searchQuery?: string): Promise<ApiResponse<any>> {
    let url = `/admin/diagnostics/${dataProduct}/history?page_size=${pageSize}`
    if (nextToken) {
      url += `&next_token=${encodeURIComponent(nextToken)}`
    }
    if (searchQuery && searchQuery.trim()) {
      url += `&search=${encodeURIComponent(searchQuery.trim())}`
    }
    return this.makeRequest(url)
  }

  // Configuration Management
  async getConfigurations(): Promise<ApiResponse<any[]>> {
    return this.makeRequest('/admin/configuration')
  }

  async getConfiguration(dataProduct: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/configuration/${dataProduct}`)
  }

  async updateConfiguration(dataProduct: string, configData: any): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/configuration/${dataProduct}`, {
      method: 'PUT',
      body: JSON.stringify(configData)
    })
  }

  async createConfiguration(dataProduct: string, configData: any): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/configuration/${dataProduct}`, {
      method: 'POST',
      body: JSON.stringify(configData)
    })
  }

  async deleteConfiguration(dataProduct: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/configuration/${dataProduct}`, {
      method: 'DELETE'
    })
  }

  // Data Product Operations
  async triggerDataCollection(dataProduct: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/admin/data-collection/trigger/${dataProduct}`, {
      method: 'POST'
    })
  }

  // Map API - Geographic queries
  async getMapLocations(dataProduct: string, bounds: {
    minLat: number,
    maxLat: number,
    minLng: number,
    maxLng: number
  }, locationIds?: string[]): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      data_product: dataProduct,
      min_lat: bounds.minLat.toString(),
      max_lat: bounds.maxLat.toString(),
      min_lng: bounds.minLng.toString(),
      max_lng: bounds.maxLng.toString()
    })
    if (locationIds && locationIds.length > 0) {
      params.set('location_ids', locationIds.join(','))
    }
    return this.makeRequest(`/admin/map/locations?${params.toString()}`)
  }

  // Device Data API (for testing)
  async getDeviceData(dataProduct: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/device/data/${dataProduct}`)
  }

  // Print Map Maker API
  async getBaseMapCatalog(): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/base-maps')
  }

  async uploadLogo(file: File): Promise<ApiResponse<any>> {
    try {
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()
      
      const formData = new FormData()
      formData.append('logo', file)
      
      const response = await fetch(`${API_BASE_URL}/api/print-maps/logos`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData
      })

      const data = response.ok ? await response.json() : null
      
      return {
        data,
        statusCode: response.status,
        error: !response.ok ? `HTTP ${response.status}: ${response.statusText}` : undefined
      }
    } catch (error) {
      return {
        statusCode: 500,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  }

  async saveDesign(designData: any): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/print-maps/designs', {
      method: 'POST',
      body: JSON.stringify(designData)
    })
  }

  async getUserDesigns(locationListId?: string): Promise<ApiResponse<any>> {
    const params = locationListId ? `?locationListId=${encodeURIComponent(locationListId)}` : ''
    return this.makeRequest(`/api/print-maps/designs${params}`)
  }

  async deleteDesign(designId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/print-maps/designs/${designId}`, {
      method: 'DELETE'
    })
  }

  async generateManufacturingFiles(designData: any): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/print-maps/generate', {
      method: 'POST',
      body: JSON.stringify(designData)
    })
  }

  async getJobStatus(jobId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/print-maps/jobs/${jobId}`, {
      method: 'GET'
    })
  }

  // Audit Log
  async getAuditLogs(params?: {
    admin_id?: string;
    target_user_id?: string;
    action_type?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    const searchParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.append(key, String(value))
      })
    }
    const qs = searchParams.toString()
    return this.makeRequest(`/admin/audit-log${qs ? `?${qs}` : ''}`)
  }

  async exportAuditLogs(params?: {
    admin_id?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<ApiResponse<any>> {
    const searchParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.append(key, String(value))
      })
    }
    const qs = searchParams.toString()
    return this.makeRequest(`/admin/audit-log/export${qs ? `?${qs}` : ''}`)
  }

  // Generic HTTP methods for theme API
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint)
  }

  async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: 'DELETE'
    })
  }

  async streamChat(
    message: string,
    sessionId: string,
    onChunk: (text: string) => void,
    dataProduct?: string
  ): Promise<void> {
    const headers = await this.getAuthHeaders();
    const bodyObj: Record<string, unknown> = { message, session_id: sessionId };
    if (dataProduct) {
      bodyObj.data_product = dataProduct;
    }
    // Use relative URL to route through CloudFront (same origin).
    // Direct API Gateway calls fail because the response-streaming
    // integration doesn't handle CORS preflight correctly.
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers,
      body: JSON.stringify(bodyObj),
    });

    if (response.status === 401) {
      throw new Error('Authentication expired. Please log in again.');
    }
    if (response.status === 504) {
      throw new Error('Response took too long. Please try again.');
    }
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split on double-newline to get complete SSE events.
      // Each event is a single "data: <json-encoded-text>" line.
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';

      for (const event of events) {
        if (!event.trim()) continue;

        // Extract the payload after "data: " prefix
        const line = event.trim();
        let payload: string;
        if (line.startsWith('data: ')) {
          payload = line.slice(6);
        } else if (line.startsWith('data:')) {
          payload = line.slice(5);
        } else {
          continue;
        }

        if (payload === '[DONE]') return;

        // The backend JSON-encodes text chunks, so every payload
        // is valid JSON (a string or an error object).
        try {
          const parsed = JSON.parse(payload);
          if (typeof parsed === 'string') {
            onChunk(parsed);
          } else if (parsed && parsed.error) {
            throw new Error(parsed.error);
          }
        } catch (e) {
          if (e instanceof SyntaxError) {
            // Fallback: treat as plain text if not valid JSON
            onChunk(payload);
          } else {
            throw e;
          }
        }
      }
    }
  }

  async getChatHistory(sessionId: string): Promise<ChatMessage[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch('/api/chat/history', {
        method: 'POST',
        headers,
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!response.ok) return [];
      const data = await response.json();
      return data?.messages || [];
    } catch {
      return [];
    }
  }
}

/**
 * Parse a string of SSE data into an array of text tokens.
 * Useful for testing SSE parsing independently.
 * Excludes [DONE] from the output.
 */
export function parseSSEStream(sseData: string): string[] {
  const tokens: string[] = [];
  const lines = sseData.split('\n\n');

  for (const line of lines) {
    if (!line.startsWith('data: ')) {
      if (line.trim()) console.warn('Skipping malformed SSE line:', line);
      continue;
    }
    const data = line.slice(6);
    if (data === '[DONE]') continue;

    try {
      const parsed = JSON.parse(data);
      if (parsed.error) {
        // Skip error objects in parsed output
        continue;
      }
    } catch (e) {
      if (e instanceof SyntaxError) {
        tokens.push(data);
      }
    }
  }

  return tokens;
}

export const apiService = new ApiService()
export default apiService